import React, { useEffect, useState } from 'react';
import {
  Button,
  CheckPicker,
  CheckTreePicker,
  Col,
  DateRangePicker,
  FlexboxGrid,
  Input,
  InputNumber,
  SelectPicker,
  Stack,
} from 'rsuite';
import { DateRange, RangeType } from 'rsuite/esm/DateRangePicker/types';
import dayjs from 'dayjs';
import _ from 'lodash';
import { ItemDataType } from 'rsuite/CascadeTree';
import { CarrierFilter, DeliveryManager, OrderStates } from '../../api/apiTypes';
import { useStoreActions, useStoreState } from '../../store/hooks';
import {
  fromApiDateRange, lastMonthRange, lastWeekRange, toApiDateRange,
} from '../../services/dateUtils';
import { Carrier } from '../../store/catalogs/types';
import AlzaIcon from '../../assets/AlzaIcon';
import AllegroIcon from '../../assets/AllegroIcon';
import MallIcon from '../../assets/MallIcon';
import { ApiGroup } from '../../api/model/ApiGroup';
import ORDER_COUNTRIES from '../../api/model/OrderCountries';

const ranges: RangeType[] = [
  {
    label: 'Yesterday',
    value: [dayjs().subtract(1, 'day').toDate(), dayjs().subtract(1, 'day').toDate()],
  },
  {
    label: 'Today',
    value: [new Date(), new Date()],
  },
  {
    label: 'Last 7 days',
    value: [dayjs().subtract(6, 'day').toDate(), dayjs().toDate()],
  },
  {
    label: 'Last week',
    value: lastWeekRange(),
  },
  {
    label: 'Last month',
    value: lastMonthRange(),
  },
];

enum FLAG {
  CROSS_DOCK_DELIVERY = 'crossDockDelivery',
  ONLY_ERRORS = 'onlyErrors',
  PRECANCELED = 'precanceled',
  ALZA_LINEHAUL = 'alzaLinehaul',
  ALZA_REGULAR_DELIVERY = 'alzaRegularDelivery',
}

const flags: { label: string, value: string }[] = [
  {
    label: 'Cross dock delivery',
    value: FLAG.CROSS_DOCK_DELIVERY,
  },
  {
    label: 'Only orders with error',
    value: FLAG.ONLY_ERRORS,
  },
  {
    label: 'ALZA Linehaul',
    value: FLAG.ALZA_LINEHAUL,
  },
  {
    label: 'ALZA Regular Delivery',
    value: FLAG.ALZA_REGULAR_DELIVERY,
  },
];

const precanceledFlag: { label: string, value: string } = {
  label: 'Orders with cancellation request',
  value: FLAG.PRECANCELED,
};

function unwrapFlags(values: string[]): Record<string, boolean> {
  return _.merge({}, ...values.map(value => ({ [value]: true })));
}

function getFilterStates(view: string): OrderStates[] {
  switch (view) {
    case 'open':
      return [OrderStates.OPEN];
    case 'shipping':
      return [OrderStates.SHIPPING];
    case 'shipped':
      return [OrderStates.SHIPPED, OrderStates.DELIVERED];
    default:
      return [];
  }
}

function shouldStatusBeLocked(view: string): boolean {
  switch (view) {
    case 'open':
    case 'shipping':
    case 'shipped':
      return true;
    default:
      return false;
  }
}

function shouldFlagBeLocked(view: string): boolean {
  switch (view) {
    case 'precanceled':
      return true;
    default:
      return false;
  }
}

function getFilterFlags(view: string): FLAG[] {
  switch (view) {
    case 'precanceled':
      return [FLAG.PRECANCELED];
    default:
      return [];
  }
}

function mapToGroup(carrier: Carrier): string {
  switch (carrier.deliveryManager) {
    case DeliveryManager.MARKETPLACE:
      return carrier.apiGroup;
    case DeliveryManager.PARTNER:
      return 'PRIVATE';
    case DeliveryManager.MPI:
      return 'MPI';
    default:
      return carrier.apiGroup;
  }
}

function mapToDeliveryManager(group: string): DeliveryManager {
  switch (group) {
    case 'PRIVATE':
      return DeliveryManager.PARTNER;
    case 'MPI':
      return DeliveryManager.MPI;
    default:
      return DeliveryManager.MARKETPLACE;
  }
}

function mapToDeliveryGroupName(group: string) {
  switch (group) {
    case 'ALLEGRO':
      return 'SHIP WITH ALLEGRO';
    case 'ALZA':
      return 'ALZA DELIVERY';
    case 'MALL_GROUP':
      return 'MALL DELIVERY';
    case 'KAUFLAND':
      return 'KAUFLAND DELIVERY';
    case 'MPI':
      return 'MPI DELIVERY';
    case 'PRIVATE':
      return 'PRIVATE TRANSPORT';
    default:
      return group;
  }
}

function mapCarriersToCheckTreePickerData(carriers: Carrier[]): ItemDataType<string>[] {
  const groupedCarriers = carriers.reduce<Record<string, ItemDataType<string>[]>>((acc, item) => {
    const group = mapToGroup(item);

    if (!acc[group]) {
      acc[group] = [];
    }

    acc[group].push({ value: item.id, label: item.name });

    return acc;
  }, {});

  const data: ItemDataType<string>[] = [];
  Object.keys(groupedCarriers).forEach(group => {
    data.push({
      value: group,
      label: mapToDeliveryGroupName(group),
      children: groupedCarriers[group],
    });
  });

  return data;
}

function getDeliveryIcon(group: string) {
  switch (group) {
    case 'ALLEGRO':
      return <AllegroIcon />;
    case 'ALZA':
      return <AlzaIcon />;
    case 'MALL_GROUP':
      return <MallIcon />;
    default:
      return undefined;
  }
}

export default function OrderFilter({ view, visible, precanceledVisible }: {
  view: string,
  visible: boolean,
  precanceledVisible: boolean
}) {
  const flagsForActiveUser: {
    label: string,
    value: string
  }[] = precanceledVisible ? [...flags, precanceledFlag] : [...flags];

  const { fetchEshops, fetchCarriers } = useStoreActions(actions => actions.catalogs);
  const { setFilter } = useStoreActions(actions => actions.orders);
  const eshops = useStoreState(state => state.catalogs.eshops);
  const carriers = useStoreState(state => state.catalogs.carriers);
  const currentFilter = useStoreState(state => state.orders.currentFilter);
  const [isStatusLocked, setStatusLocked] = useState(false);
  const [shouldAutoApply, setShouldAutoApply] = useState(false);
  const [isFlagLocked, setFlagLocked] = useState(false);

  const { filter } = currentFilter;
  const [selectedStates, setSelectedStates] = useState<OrderStates[]>(filter.states);
  const [selectedEshops, setSelectedEshops] = useState<string[]>(filter.eshops ?? []);
  const [selectedFlags, setSelectedFlags] = useState<string[]>([]);

  const filterCarriers = filter.carriers.map(c => `${c.apiGroup};${c.carrier};${c.deliveryManager}`);
  const [selectedCarriers, setSelectedCarriers] = useState<string[]>(filterCarriers);
  const [expeditionDate, setExpeditionDate] = useState<DateRange | null>(
    fromApiDateRange(filter.expedition),
  );
  const [confirmedDate, setConfirmedDate] = useState<DateRange | null>(
    fromApiDateRange(filter.confirmed),
  );
  const [deliveredDate, setDeliveredDate] = useState<DateRange | null>(
    fromApiDateRange(filter.delivered),
  );
  const [shippingDate, setShippingDate] = useState<DateRange | null>(
    fromApiDateRange(filter.shipping),
  );
  const [returnedDate, setReturnedDate] = useState<DateRange | null>(
    fromApiDateRange(filter.returned),
  );
  const [shippedDate, setShippedDate] = useState<DateRange | null>(
    fromApiDateRange(filter.shipped),
  );
  const [cancelledDate, setCancelledDate] = useState<DateRange | null>(
    fromApiDateRange(filter.cancelled),
  );

  const [orderValueFrom, setOrderValueFrom] = useState<number | string | null>(filter.orderValue?.from ?? '');
  const [orderValueTo, setOrderValueTo] = useState<number | string | null>(filter.orderValue?.to ?? '');

  const [originalOrderId, setOriginalOrderId] = useState(filter.originalOrderId ?? '');
  const [partnerOrderId, setPartnerOrderId] = useState(filter.partnerOrderId ?? '');
  const [orderCountry, setOrderCountry] = useState<string | null>(
    filter.orderCountry ?? null,
  );

  const applyFilters = () => {
    const global = currentFilter.filter.globalFilter;

    function parseCarriers(ids: string[]): CarrierFilter[] {
      const result: CarrierFilter[] = [];

      ids.forEach(id => {
        const split = id.split(';');
        const group = split[0];
        const carrier = split[1];
        const deliveryManager = split[2];

        if (carrier && deliveryManager in DeliveryManager && group in ApiGroup) {
          result.push({
            apiGroup: group as ApiGroup,
            deliveryManager: deliveryManager as DeliveryManager,
            carrier,
          });
        } else {
          const groupDeliveryManager = mapToDeliveryManager(group);
          carriers
            .filter(value => {
              if (groupDeliveryManager === DeliveryManager.MARKETPLACE) {
                return value.deliveryManager === groupDeliveryManager && value.apiGroup === group;
              }

              return value.deliveryManager === groupDeliveryManager;
            })
            .forEach(value => {
              result.push({
                apiGroup: value.apiGroup,
                deliveryManager: value.deliveryManager,
                carrier: value.name,
              });
            });
        }
      });

      return result;
    }

    const newFilter = {
      ...currentFilter,
      page: 1,
      filter: {
        globalFilter: global,
        states: selectedStates,
        eshops: selectedEshops,
        expedition: toApiDateRange(expeditionDate),
        confirmed: toApiDateRange(confirmedDate),
        delivered: toApiDateRange(deliveredDate),
        shipping: toApiDateRange(shippingDate),
        shipped: toApiDateRange(shippedDate),
        returned: toApiDateRange(returnedDate),
        cancelled: toApiDateRange(cancelledDate),
        originalOrderId,
        orderValue: {
          from: orderValueFrom ? +orderValueFrom : null,
          to: orderValueTo ? +orderValueTo : null,
        },
        partnerOrderId,
        carriers: parseCarriers(selectedCarriers),
        orderCountry: orderCountry ?? undefined,
        ...unwrapFlags(selectedFlags),
      },
    };
    setFilter(newFilter);
  };

  useEffect(() => {
    fetchEshops();
    fetchCarriers();
  }, [fetchEshops, fetchCarriers]);

  useEffect(() => {
    setSelectedStates(getFilterStates(view));
    setStatusLocked(shouldStatusBeLocked(view));
    setSelectedFlags(getFilterFlags(view));
    setFlagLocked(shouldFlagBeLocked(view));
    setShouldAutoApply(true);
  }, [view]);

  useEffect(() => {
    if (shouldAutoApply) {
      applyFilters();
      setShouldAutoApply(false);
    }
  }, [selectedStates, selectedFlags, shouldAutoApply]);

  const states = Object.values(OrderStates)
    .filter(s => s !== OrderStates.RESERVATION)
    .sort((a, b) => a.localeCompare(b))
    .map(s => ({ value: s, label: s }));

  const clearFilters = () => {
    if (!isStatusLocked) {
      setSelectedStates([]);
    }

    setSelectedEshops([]);
    setExpeditionDate(null);
    setConfirmedDate(null);
    setDeliveredDate(null);
    setShippingDate(null);
    setReturnedDate(null);
    setShippedDate(null);
    setCancelledDate(null);
    setOriginalOrderId('');
    setPartnerOrderId('');
    setOrderValueFrom('');
    setOrderValueTo('');
    setSelectedCarriers([]);
    setSelectedFlags([]);
    setOrderCountry(null);

    setFilter({
      ...currentFilter,
      page: 1,
      filter: {
        globalFilter: currentFilter.filter.globalFilter,
        states: [],
        eshops: [],
        expedition: undefined,
        confirmed: undefined,
        delivered: undefined,
        shipping: undefined,
        shipped: undefined,
        returned: undefined,
        cancelled: undefined,
        originalOrderId: '',
        orderValue: undefined,
        partnerOrderId: '',
        carriers: [],
        crossDockDelivery: undefined,
        alzaLinehaul: undefined,
        alzaRegularDelivery: undefined,
        orderCountry: undefined,
      },
    });
  };

  if (!visible) {
    return null;
  }

  return (
    <FlexboxGrid align="middle" justify="start" className="flex-box-filters mt-10">
      <FlexboxGrid.Item as={Col} lg={2} colspan={8}>
        Marketplace
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={4} colspan={16}>
        <CheckPicker
          data={eshops}
          labelKey="name"
          valueKey="code"
          searchable={false}
          block
          value={selectedEshops}
          onChange={setSelectedEshops}
        />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={2} colspan={8}>
        Suggested expedition
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={4} colspan={16}>
        <DateRangePicker
          isoWeek
          placeholder="Select"
          block
          ranges={ranges}
          value={expeditionDate}
          onChange={setExpeditionDate}
          placement="bottomEnd"
        />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={2} colspan={8}>
        Opened
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={4} colspan={16}>
        <DateRangePicker
          isoWeek
          placeholder="Select"
          block
          value={confirmedDate}
          onChange={setConfirmedDate}
        />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={2} colspan={8}>
        Delivered
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={4} colspan={16}>
        <DateRangePicker
          isoWeek
          placeholder="Select"
          block
          value={deliveredDate}
          onChange={setDeliveredDate}
          ranges={ranges}
          placement="bottomEnd"
        />
      </FlexboxGrid.Item>

      <FlexboxGrid.Item as={Col} lg={2} colspan={8}>
        Status
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={4} colspan={16}>
        <CheckPicker
          searchable={false}
          data={states}
          block
          disabled={isStatusLocked}
          value={selectedStates}
          onChange={setSelectedStates}
        />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={2} colspan={8}>
        Order value
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={4} colspan={16}>
        <Stack spacing={5}>
          <InputNumber value={orderValueFrom} onChange={setOrderValueFrom} min={0} />
          <span>-</span>
          <InputNumber value={orderValueTo} onChange={setOrderValueTo} min={0} />
        </Stack>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={2} colspan={8}>
        Shipping
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={4} colspan={16}>
        <DateRangePicker
          isoWeek
          placeholder="Select"
          block
          value={shippingDate}
          onChange={setShippingDate}
          ranges={ranges}
          placement="bottomEnd"
        />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={2} colspan={8}>
        Returned
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={4} colspan={16}>
        <DateRangePicker
          isoWeek
          placeholder="Select"
          block
          value={returnedDate}
          onChange={setReturnedDate}
          ranges={ranges}
          placement="bottomEnd"
        />
      </FlexboxGrid.Item>

      <FlexboxGrid.Item as={Col} lg={2} colspan={8}>
        Order Number (MP)
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={4} colspan={16}>
        <Input
          value={originalOrderId}
          onChange={setOriginalOrderId}
        />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={2} colspan={8}>
        Order Number (EXT)
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={4} colspan={16}>
        <Input value={partnerOrderId} onChange={setPartnerOrderId} />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={2} colspan={8}>
        Shipped
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={4} colspan={16}>
        <DateRangePicker
          isoWeek
          placeholder="Select"
          block
          value={shippedDate}
          onChange={setShippedDate}
          ranges={ranges}
          placement="bottomEnd"
        />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={2} colspan={8}>
        Cancelled
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={4} colspan={16}>
        <DateRangePicker
          isoWeek
          placeholder="Select"
          block
          value={cancelledDate}
          onChange={setCancelledDate}
          ranges={ranges}
          placement="bottomEnd"
        />
      </FlexboxGrid.Item>

      <FlexboxGrid.Item as={Col} lg={2} colspan={8}>
        Carrier
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={4} colspan={16}>
        <CheckTreePicker
          searchable={false}
          data={mapCarriersToCheckTreePickerData(carriers)}
          value={selectedCarriers}
          block
          onChange={(values) => setSelectedCarriers(values as string[])}
          renderTreeNode={nodeData => (
            <span>
              {getDeliveryIcon(nodeData.value as string)}
              {' '}
              {nodeData.label}
            </span>
          )}
        />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={2} colspan={8}>
        Flags
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={4} colspan={16}>
        <CheckPicker
          searchable
          block
          data={flagsForActiveUser}
          valueKey="value"
          labelKey="label"
          value={selectedFlags}
          onChange={setSelectedFlags}
          disabled={isFlagLocked}
        />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={2} colspan={8}>
        Order country
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={4} colspan={16}>
        <SelectPicker
          searchable={false}
          placeholder="Select"
          block
          data={ORDER_COUNTRIES}
          valueKey="value"
          labelKey="label"
          value={orderCountry}
          onChange={setOrderCountry}
        />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item as={Col} lg={6} colspan={24}>
        <Stack spacing={10} alignItems="flex-end" justifyContent="flex-end">
          <Button appearance="primary" style={{ width: 80 }} onClick={applyFilters}>Apply</Button>
          <Button appearance="primary" style={{ width: 80 }} onClick={clearFilters}>Clear</Button>
        </Stack>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
}
